import * as React from "react";
import {useMemo, useRef} from "react";
import {Grid2 as Grid, Typography} from "@mui/material";
import {VisibilityOff, WarningAmberOutlined} from "@mui/icons-material";
import {convertToThreeDigitString, ErrorsShape} from "@/lib/utils";
import {ChangeHandler, NamedValueHandler} from "@/lib/modelstate";
import {
    AddressDTO,
    CalculationStatus,
    CardinalDirection,
    CompanyDisplayDTO,
    OrderDataDTO,
    UserDisplayDTO
} from "@/api/data-contracts";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import {formatAddressToMultiline} from "@/model/address";
import {CardEx} from "@/lib/components/CardEx";
import i18n from "@/i18n";
import RequestDateFields from "@/common/request-cards/RequestDateFields";
import AutocompleteInputField from "@/lib/components/inputfields/auto-complete-input-field";
import {countryOptions, useEmployees} from "@/datacaches";
import {Constants} from "@/api/constants";
import {useTranslation} from "react-i18next";
import { CommonI18N, CompanyI18N, RequestI18N } from "@/translations";
import {OrderDataType} from "@/model/orderData";
import {GroupBox} from "@/lib/components/GroupBox";
import RadioButtonInputField from "@/lib/components/inputfields/radio-button-field";
import SelectInputField from "@/lib/components/inputfields/select-input-field";
import CheckboxInputField from "@/lib/components/inputfields/checkbox-input-field";
import {useAuth} from "@/app/auth/AuthContext";
import DeliveryInstructionTooltip from "@/lib/components/DeliveryInstructionTooltip";

interface RequestInfo {
    customerReferenceNumber?: string;
    deliveryAddress?: AddressDTO;
    hideDeliveryAddress?: boolean;
    contactPerson?: UserDisplayDTO;
    additionalInformation?: string;
    validUntil?: string | Date;
    company?: CompanyDisplayDTO;
    orderData?: OrderDataDTO;
    distanceMeters?: number;
    directionToRequester?: CardinalDirection;
    deliveryDistanceStatus?: CalculationStatus;
}

export const RequestCard = ({
                                model,
                                errors,
                                onChange,
                                readonly,
                                mode
                            }: {
    model?: RequestInfo;
    onChange?: ChangeHandler;
    errors?: ErrorsShape<RequestInfo>;
    readonly?: boolean;
    mode: OrderDataType;
}) => {
    const {t} = useTranslation();
    const {company: tenant} = useAuth();
    const [employees] = useEmployees();
    const locale = i18n.language;
    const readonlyEx = readonly || mode === "quote";
    const companyAddressRef = useRef<AddressDTO | undefined>(undefined);

    const availableUsers = useMemo(() => employees, [employees]);

    const countrySelectOptions = useMemo(
            () =>
                    countryOptions.map((option) => ({
                        label: option && t(CommonI18N.enum.country[option]),
                        value: option
                    })),
            [t]
    );

    const isPackagingAddress = model?.deliveryAddress?.companyAddress === false;
    const deliveryInstructionReadonly = readonlyEx || (mode === "request" && !isPackagingAddress);

    return (
            <CardEx
                    title={
                        mode === "request"
                                ? t(RequestI18N.details.page.title)
                                : t(RequestI18N.details.page.fromCompanyTitle, {
                                    companyName: model?.company?.name ?? ""
                                })
                    }
            >
                <Grid container spacing={2} direction="column">
                    <Grid size={{sm: 12}} container direction="row" spacing={2}>
                        <Grid size={{sm: 6}}>
                            <StringInputField
                                    name="customerReferenceNumber"
                                    value={model?.customerReferenceNumber}
                                    label={t(RequestI18N.details.field.customerRequestNumber)}
                                    error={errors?.customerReferenceNumber}
                                    maxChars={Constants.CUSTOMER_REFERENCE_NUMBER_LENGTH_MAX}
                                    activatePrefix={true}
                                    onChange={onChange}
                                    prefixValue={
                                        model?.company?.number == null
                                                ? t(CommonI18N.message.notGiven)
                                                : `${convertToThreeDigitString(model?.company?.number)} -`
                                    }
                                    disabled={readonlyEx}
                            />
                        </Grid>
                        <RequestDateFields
                                nameValidUntil="validUntil"
                                nameDeliveryDateFrom="orderData.deliveryDateFrom"
                                nameDeliveryDateTo="orderData.deliveryDateTo"
                                validUntil={model?.validUntil}
                                deliveryDateFrom={model?.orderData?.deliveryDateFrom}
                                deliveryDateTo={model?.orderData?.deliveryDateTo}
                                errors={{
                                    validUntil: errors?.validUntil,
                                    deliveryDateFrom: errors?.orderData?.deliveryDateFrom,
                                    deliveryDateTo: errors?.orderData?.deliveryDateTo
                                }}
                                onChange={onChange}
                                disabled={readonlyEx}
                        />
                    </Grid>

                    <Grid size={{sm: 12}}>
                        <RadioButtonInputField
                                name="orderData.rfi"
                                label={t(RequestI18N.details.field.rfi)}
                                value={model?.orderData?.rfi ?? false}
                                onChange={onChange as NamedValueHandler}
                                disabled={readonlyEx}
                                options={[
                                    {label: t(RequestI18N.details.field.rfiOrderRequest), value: false},
                                    {label: t(RequestI18N.details.field.rfiTenderRequest), value: true}
                                ]}
                        />
                    </Grid>

                    {mode === "request" && (
                            <Grid size={{sm: 12}} sx={{display: "flex", alignItems: "center", gap: 1}}>
                                <CheckboxInputField
                                        name="deviatingDeliveryAddress"
                                        label={t(RequestI18N.details.field.deviatingDeliveryAddress)}
                                        value={isPackagingAddress}
                                        onChange={({value}) => {
                                            if (!onChange) {
                                                return;
                                            }
                                            if (value) {
                                                if (model?.deliveryAddress?.companyAddress !== false) {
                                                    companyAddressRef.current = model?.deliveryAddress;
                                                }
                                                onChange({
                                                    name: "deliveryAddress",
                                                    value: {
                                                        id: undefined,
                                                        companyAddress: false,
                                                        street: "",
                                                        zip: "",
                                                        city: "",
                                                        country: undefined as unknown as AddressDTO["country"],
                                                        deliveryInstruction: {}
                                                    }
                                                });
                                                if (model?.hideDeliveryAddress == null) {
                                                    onChange({name: "hideDeliveryAddress", value: false});
                                                }
                                                return;
                                            }
                                            const restoreAddress = companyAddressRef.current ?? model?.deliveryAddress;
                                            onChange({name: "hideDeliveryAddress", value: false});
                                            if (restoreAddress) {
                                                onChange({
                                                    name: "deliveryAddress",
                                                    value: {
                                                        ...restoreAddress,
                                                        companyAddress: true
                                                    } as AddressDTO
                                                });
                                                return;
                                            }
                                            if (tenant) {
                                                onChange({name: "deliveryAddress", value: undefined});
                                            }
                                        }}
                                        disabled={readonlyEx}
                                />
                                {isPackagingAddress && (
                                        <DeliveryInstructionTooltip
                                                deliveryInstruction={model?.deliveryAddress?.deliveryInstruction}
                                        />
                                )}
                            </Grid>
                    )}

                    {mode === "request" && isPackagingAddress && (
                            <Grid container spacing={2} size={{sm: 12}}>
                                <Grid size={{sm: 8, xs: 12}}>
                                    <StringInputField
                                            name="deliveryAddress.street"
                                            value={model?.deliveryAddress?.street}
                                            label={t(CompanyI18N.form.field.street)}
                                            error={errors?.deliveryAddress?.street}
                                            maxChars={Constants.STREET_LENGTH_MAX}
                                            onChange={onChange}
                                            disabled={readonlyEx}
                                    />
                                </Grid>
                                <Grid size={{sm: 4, xs: 12}}>
                                    <StringInputField
                                            name="deliveryAddress.houseNumber"
                                            value={model?.deliveryAddress?.houseNumber}
                                            label={t(CompanyI18N.form.field.houseNumber)}
                                            error={errors?.deliveryAddress?.houseNumber}
                                            maxChars={Constants.HOUSE_NUMBER_LENGTH_MAX}
                                            onChange={onChange}
                                            disabled={readonlyEx}
                                    />
                                </Grid>
                                <Grid size={{sm: 6, xs: 12}}>
                                    <StringInputField
                                            name="deliveryAddress.zip"
                                            value={model?.deliveryAddress?.zip}
                                            label={t(CompanyI18N.form.field.postalCode)}
                                            error={errors?.deliveryAddress?.zip}
                                            maxChars={Constants.ZIP_LENGTH_MAX}
                                            onChange={onChange}
                                            disabled={readonlyEx}
                                    />
                                </Grid>
                                <Grid size={{sm: 6, xs: 12}}>
                                    <StringInputField
                                            name="deliveryAddress.city"
                                            value={model?.deliveryAddress?.city}
                                            label={t(CompanyI18N.form.field.city)}
                                            error={errors?.deliveryAddress?.city}
                                            maxChars={Constants.CITY_LENGTH_MAX}
                                            onChange={onChange}
                                            disabled={readonlyEx}
                                    />
                                </Grid>
                                <Grid size={{sm: 6, xs: 12}}>
                                    <SelectInputField
                                            name="deliveryAddress.country"
                                            label={t(CompanyI18N.form.field.country)}
                                            value={model?.deliveryAddress?.country}
                                            error={errors?.deliveryAddress?.country}
                                            options={countrySelectOptions}
                                            displayEmpty={true}
                                            onChange={onChange}
                                            disabled={readonlyEx}
                                    />
                                </Grid>
                                <Grid size={{xs: 12}}>
                                    <CheckboxInputField
                                            name="hideDeliveryAddress"
                                            label={t(RequestI18N.details.field.hideDeliveryAddress)}
                                            value={model?.hideDeliveryAddress}
                                            onChange={onChange}
                                            disabled={readonlyEx}
                                    />
                                </Grid>
                            </Grid>
                    )}

                    {(mode !== "request" || !isPackagingAddress) && (
                            <Grid size={{sm: 12}}>
                                <StringInputField
                                        name="deliveryAddressStreet"
                                        label={t(RequestI18N.details.field.deliveryAddress)}
                                        value={
                                            model?.hideDeliveryAddress
                                                    ? t(RequestI18N.details.message.hiddenDeliveryAddressHint)
                                                    : formatAddressToMultiline(t, model?.deliveryAddress, locale)
                                        }
                                        startAdornment={model?.hideDeliveryAddress ?
                                                <VisibilityOff fontSize="small"/> : undefined}
                                        endAdornment={<DeliveryInstructionTooltip
                                                deliveryInstruction={model?.deliveryAddress?.deliveryInstruction}/>}
                                        multiline
                                        disabled
                                />
                            </Grid>
                    )}
                    {mode === "quote" && model?.deliveryDistanceStatus != null && (
                            <Grid size={{sm: 12}} container direction="row" spacing={2}>
                                {model.deliveryDistanceStatus === "PENDING" && (
                                        <Grid size={{sm: 12}}>
                                            <Typography variant="body2" color="text.secondary">
                                                {t(RequestI18N.details.field.deliveryDistancePending)}
                                            </Typography>
                                        </Grid>
                                )}
                                {model.deliveryDistanceStatus === "ERROR" && (
                                        <Grid size={{sm: 12}} sx={{display: "flex", alignItems: "center", gap: 1}}>
                                            <WarningAmberOutlined color="warning" fontSize="small"/>
                                            <Typography variant="body2" color="warning.main">
                                                {t(RequestI18N.details.field.deliveryDistanceError)}
                                            </Typography>
                                        </Grid>
                                )}
                                {model.deliveryDistanceStatus === "CALCULATED" && (
                                        <>
                                            {model.distanceMeters != null && (
                                                    <Grid size={{sm: 6}}>
                                                        <StringInputField
                                                                name="distanceToRequester"
                                                                label={t(RequestI18N.details.field.distanceToRequester)}
                                                                value={`${Math.round(model.distanceMeters / 1000)} km`}
                                                                disabled
                                                        />
                                                    </Grid>
                                            )}
                                            {model.directionToRequester != null && (
                                                    <Grid size={{sm: 6}}>
                                                        <StringInputField
                                                                name="directionToRequester"
                                                                label={t(RequestI18N.details.field.directionToRequester)}
                                                                value={t(CommonI18N.enum.cardinalDirection[model.directionToRequester])}
                                                                disabled
                                                        />
                                                    </Grid>
                                            )}
                                        </>
                                )}
                            </Grid>
                    )}

                    {!deliveryInstructionReadonly && (
                            <Grid size={{sm: 12}}>
                                <GroupBox title={t(CompanyI18N.form.field.deliveryInstructionsTitle)}
                                          name="additionalInformation">
                                    <Grid container spacing={2} direction="row">
                                        <Grid container spacing={2} size={{sm: 6, xs: 12}} direction="column">
                                            <Grid size={{xs: 12}}>
                                                <StringInputField
                                                        name="deliveryAddress.deliveryInstruction.mondayInstruction"
                                                        value={model?.deliveryAddress?.deliveryInstruction?.mondayInstruction}
                                                        label={t(CompanyI18N.form.field.monday)}
                                                        error={errors?.deliveryAddress?.deliveryInstruction?.mondayInstruction}
                                                        maxChars={Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX}
                                                        onChange={onChange}
                                                        disabled={deliveryInstructionReadonly}
                                                />
                                            </Grid>
                                            <Grid size={{xs: 12}}>
                                                <StringInputField
                                                        name="deliveryAddress.deliveryInstruction.tuesdayInstruction"
                                                        value={model?.deliveryAddress?.deliveryInstruction?.tuesdayInstruction}
                                                        label={t(CompanyI18N.form.field.tuesday)}
                                                        error={errors?.deliveryAddress?.deliveryInstruction?.tuesdayInstruction}
                                                        maxChars={Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX}
                                                        onChange={onChange}
                                                        disabled={deliveryInstructionReadonly}
                                                />
                                            </Grid>
                                            <Grid size={{xs: 12}}>
                                                <StringInputField
                                                        name="deliveryAddress.deliveryInstruction.wednesdayInstruction"
                                                        value={model?.deliveryAddress?.deliveryInstruction?.wednesdayInstruction}
                                                        label={t(CompanyI18N.form.field.wednesday)}
                                                        error={errors?.deliveryAddress?.deliveryInstruction?.wednesdayInstruction}
                                                        maxChars={Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX}
                                                        onChange={onChange}
                                                        disabled={deliveryInstructionReadonly}
                                                />
                                            </Grid>
                                            <Grid size={{xs: 12}}>
                                                <StringInputField
                                                        name="deliveryAddress.deliveryInstruction.thursdayInstruction"
                                                        value={model?.deliveryAddress?.deliveryInstruction?.thursdayInstruction}
                                                        label={t(CompanyI18N.form.field.thursday)}
                                                        error={errors?.deliveryAddress?.deliveryInstruction?.thursdayInstruction}
                                                        maxChars={Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX}
                                                        onChange={onChange}
                                                        disabled={deliveryInstructionReadonly}
                                                />
                                            </Grid>
                                            <Grid size={{xs: 12}}>
                                                <StringInputField
                                                        name="deliveryAddress.deliveryInstruction.fridayInstruction"
                                                        value={model?.deliveryAddress?.deliveryInstruction?.fridayInstruction}
                                                        label={t(CompanyI18N.form.field.friday)}
                                                        error={errors?.deliveryAddress?.deliveryInstruction?.fridayInstruction}
                                                        maxChars={Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX}
                                                        onChange={onChange}
                                                        disabled={deliveryInstructionReadonly}
                                                />
                                            </Grid>
                                            <Grid size={{xs: 12}}>
                                                <StringInputField
                                                        name="deliveryAddress.deliveryInstruction.saturdayInstruction"
                                                        value={model?.deliveryAddress?.deliveryInstruction?.saturdayInstruction}
                                                        label={t(CompanyI18N.form.field.saturday)}
                                                        error={errors?.deliveryAddress?.deliveryInstruction?.saturdayInstruction}
                                                        maxChars={Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX}
                                                        onChange={onChange}
                                                        disabled={deliveryInstructionReadonly}
                                                />
                                            </Grid>
                                            <Grid size={{xs: 12}}>
                                                <StringInputField
                                                        name="deliveryAddress.deliveryInstruction.sundayInstruction"
                                                        value={model?.deliveryAddress?.deliveryInstruction?.sundayInstruction}
                                                        label={t(CompanyI18N.form.field.sunday)}
                                                        error={errors?.deliveryAddress?.deliveryInstruction?.sundayInstruction}
                                                        maxChars={Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX}
                                                        onChange={onChange}
                                                        disabled={deliveryInstructionReadonly}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={2} size={{sm: 6, xs: 12}} direction="column">
                                            <Grid size={{xs: 12}}>
                                                <CheckboxInputField
                                                        name="deliveryAddress.deliveryInstruction.timeSlotBookingRequired"
                                                        label={t(CompanyI18N.form.field.isTimeSlotBooking)}
                                                        onChange={onChange}
                                                        value={model?.deliveryAddress?.deliveryInstruction?.timeSlotBookingRequired}
                                                        error={errors?.deliveryAddress?.deliveryInstruction?.timeSlotBookingRequired}
                                                        disabled={deliveryInstructionReadonly}
                                                />
                                            </Grid>
                                            <Grid size={{xs: 12}}>
                                                <StringInputField
                                                        multiline={true}
                                                        name="deliveryAddress.deliveryInstruction.arrivalNotification"
                                                        value={model?.deliveryAddress?.deliveryInstruction?.arrivalNotification}
                                                        label={t(CompanyI18N.form.field.preAdviceNote)}
                                                        error={errors?.deliveryAddress?.deliveryInstruction?.arrivalNotification}
                                                        maxChars={Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX}
                                                        onChange={onChange}
                                                        disabled={deliveryInstructionReadonly}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </GroupBox>
                            </Grid>
                    )}

                    {mode === "quote" && (
                            <>
                                <Grid size={{sm: 12}}>
                                    <StringInputField
                                            name="deliveryAddressStreet"
                                            label={t(RequestI18N.details.field.companyAddress)}
                                            value={formatAddressToMultiline(t, model?.company?.companyAddress, locale)}
                                            multiline
                                            disabled
                                    />
                                </Grid>
                                <Grid size={{sm: 12}} container direction="row" spacing={2}>
                                    <Grid size={{sm: 6}}>
                                        <StringInputField
                                                name="contactPersonTelephone"
                                                label={t(RequestI18N.details.field.phoneNumber)}
                                                value={model?.contactPerson?.telephone}
                                                multiline
                                                disabled
                                        />
                                    </Grid>
                                    <Grid size={{sm: 6}}>
                                        <StringInputField
                                                name="contactPersonTelefax"
                                                label={t(RequestI18N.details.field.telefax)}
                                                value={model?.contactPerson?.fax}
                                                multiline
                                                disabled
                                        />
                                    </Grid>
                                </Grid>
                            </>
                    )}
                    <Grid size={{sm: 12}}>
                        <AutocompleteInputField
                                name="contactPerson"
                                label={t(RequestI18N.details.field.contactPerson)}
                                onChange={onChange}
                                value={model?.contactPerson}
                                error={errors?.contactPerson}
                                options={availableUsers}
                                getOptionLabel={(option) =>
                                        (option && `${option.firstName} ${option.lastName} <${option.email}>`) ?? ""
                                }
                                disabled={readonlyEx}
                        />
                    </Grid>
                </Grid>
            </CardEx>
    );
};
